<template>
	<r-standard-modal-form class="standard-modal-form" @closeModal="closeModal">
		<div slot="title" class="access-control-modal-header">
			<div class="p-typography-bold-18">Workbook Access</div>
			<a class="sharing-and-permissions-link p-typography-regular-14" :href="sharingAndPermissionsLink" target="_blank">Learn more about sharing and permissions</a>
		</div>
		<div slot="body" class="access-control-modal-body">
			<div class="input-instruction p-typography-bold-13">Enter an email or name</div>
			<r-pop-menu
				ref="resultPop"
				class="search-container"
				:closeOnClick="false"
				:openOnClick="false"
				:closeOnContentPointerDown="true"
				:spaceBetweenActivatorAndContent="3"
				:activatorDimensionPollingIntervalMs="100"
			>
				<div 
					slot="activator"
					class="search-input-container"
				>
					<input 
						:class="{
							'search-input': true,
							'show-new-permission-pop': searchTerm.length > 0
						}"
						type="text"
						ref="searchField"
						placeholder='"jbrown@inotech.com" or "Jack Brown"'
						@focus="focused"
						@blur="blurred"
						@keydown.tab.stop.prevent="tabbed($event)"
						v-model="searchTerm"
					/>
					<r-pop-menu
						v-if="searchTerm.length > 0"
						class="new-permission-pop"
						containerWidth="300px"
						:closeOnContentPointerDown="true"
						:spaceBetweenActivatorAndContent="3"
						:activatorDimensionPollingIntervalMs="100"
						:disabled="permissionLevels.length === 1"
					>
						<div 
							slot="activator"
							class="permission-selection-activator"
							:class="{
								disabled: permissionLevels.length === 1,
							}"
						>
							<div class="permission-selection-activator-selector">
								<div class="permission-selection-activator-text">{{ newAccessLevelLabel }}</div>
								<r-standard-icon
									v-if="permissionLevels.length > 1"
									icon="smallArrowDown"
									color="grey-300"
									:size="{
										height: 6,
										width: 9,
									}"
								>
								</r-standard-icon>
							</div>
						</div>
						<r-pop-list
							slot="content"
						>
							<r-pop-list-item 
								class="permission-selection-item"
								v-for="permissionLevel in permissionLevels"
								:key="permissionLevel.key"
								:enabled="workbookHasPermission(permissionLevel.permission)"
								@pointerdown="setNewAccessLevel(permissionLevel.key, permissionLevel.hasEntitlement)"
							>
								<div class="possible-checkmark">
									<r-standard-icon
										class="checkmark-icon"
										v-if="permissionLevel.key === newPermission"
										icon="checkMark"
									></r-standard-icon>
								</div>
								<div class="permission-item-container">
									<div class="permission-label">{{ permissionLevel.label }}</div>
									<div class="permission-description">{{ permissionLevel.description }}</div>
									<div class="permission-icon-wrapper">
										<p-icon overrideColor :icon="!permissionLevel.hasEntitlement ? 'upgrade' : 'blank'" />
									</div>
								</div>
							</r-pop-list-item>
						</r-pop-list>
					</r-pop-menu>
				</div>
				<div
					slot="content"
					class="search-results"
				>	
					<div v-if="searchTerm.length === 0 || (searchResults === null && !searchTermQueue.isRunningAsyncAction())" class="search-pop-prompt">
						Start typing to search for Users
					</div>
					<div v-else-if="searchTermQueue.isRunningAsyncAction()" class="search-pop-prompt">
						Searching Users<r-loading-ellipsis></r-loading-ellipsis>
					</div>
					<r-pop-list v-else-if="searchResults && searchResults.length > 0">
						<r-pop-list-item 
							class="search-result-item"
							:class="{
								'double-label': user.displayLabel !== user.emailAddress,
							}"
							v-for="user, index in searchResults"
							:key="user.userGUID"
							@pointerdown="resultSelected(user)"
						>
							<div class="search-result">
								<div 
									v-if="user.displayLabel !== user.emailAddress"
									class="result-label-top"
								>
									{{ user.displayLabel }}
								</div>
								<div 
									class="result-label-sub"
								>
									{{ user.emailAddress }}
								</div>
							</div>
						</r-pop-list-item >
					</r-pop-list>
					<div v-else-if="searchTerm.length > 0" class="search-pop-prompt">
						No users found
					</div>
				</div>
			</r-pop-menu>

			<r-button 
				theme="dark-blue" 
				class="save-button" 
				:enabled="shareButtonEnabled"
				:processing="shareWaiting"
				processingTheme="dots"
				fullWidth
				@click="addShareUser"
			>
				<span class="button-text">Share</span>
			</r-button>
			<div class="top-message-container">
				<transition name="fade-out-slow">
					<span v-if="topMessage" v-text="topMessage.text" class="invite-message" :class="{'warning': topMessage.warning}"></span>
				</transition>
			</div>

			<div 
				class="user-list-container"
			>
				<div class="user-list-title">Collaborators in this Workbook</div>
				<div
					v-if="!loadingUsers"
					class="user-list"
				>
					<div 
						class="user-list-item"
						v-for="user, index in sortedAccessControlUsers"
						:key="user.userGUID"
					>
						<access-control-user-item
							:dataStore="dataStore"
							:user="user"
							:permissionLicenses="permissionLicenses"
						>
						</access-control-user-item>
					</div>
				</div>
				<div
					v-else
					class="user-list"
				>
					<div 
						class="user-list-item"
						v-for="dummy, index in loadingUsersList"
						:key="index"
					>
						<div class="loading-block"></div>
					</div>
				</div>
			</div>

			<!-- <r-input-field>
				<span slot="label" class="input-field-title">Or copy link</span>
				<div class="shareable-link-container" slot="input">
					<span class="shareable-link">
						<input readonly ref="link" v-model="shareableLink">
						</input>
					</span>
					<span class="copy-button" @click="copyLink">
						COPY
					</span>
				</div>
			</r-input-field> -->
			<div class="footer-area">
				<transition name="fade-out-slow">
					<div 
						class="footer-title"
						:class="{
							'success-message': changedDefaultAccess !== null && changedDefaultAccess.wasSet,
							'warn-message': changedDefaultAccess !== null && !changedDefaultAccess.wasSet,
						}"
					>
						{{ footerTitleText }}
					</div>
				</transition>
				<r-pop-menu
					containerWidth="300px"
					:disabled="!workbookHasPermission('access_setDefault')"
				>
					<div 
						slot="activator"
						class="footer-activator"
						:class="{
							disabled: !workbookHasPermission('access_setDefault'),
						}"
					>
						<r-standard-icon 
							:icon="defaultPermissionIcon" 
							color="grey-500"
						/>
						<div class="footer-activator-text">{{ defaultPermissionText }}</div>
						<r-standard-icon 
							v-if="workbookHasPermission('access_setDefault')"
							icon="smallArrowDown"
							:size="{
								height: 6,
								width: 9,
							}"
							color="grey-300"
						/>
					</div>
					<r-pop-list slot="content" class="options-menu">
						<r-pop-list-item 
							class="option-item"
							@click="setDefaultPermissionLevel('private', true)"
						>
							<div class="possible-checkmark">
								<r-standard-icon
									class="checkmark-icon"
									v-if="defaultPermissionKey === 'private'"
									icon="checkMark"
								></r-standard-icon>
							</div>
							<div class="default-item-container">
								<div class="item-title">Need an invite to view</div>
								<div class="item-subtitle">Users cannot see this Workbook or its data unless specifically invited.</div>
							</div>
						</r-pop-list-item>
						<r-pop-list-item 
							class="option-item"
							@click="setDefaultPermissionLevel('viewer', permissionLicenses.viewer)"
						>
							<div class="possible-checkmark">
								<r-standard-icon
									class="checkmark-icon"
									v-if="defaultPermissionKey === 'viewer'"
									icon="checkMark"
								></r-standard-icon>
							</div>
							<div class="default-item-container">
								<div class="item-title">Have view access</div>
								<div class="item-subtitle">All users can view this Workbook and comments, but they cannot add or edit any comments or data.</div>
								<div class="item-icon-wrapper">
									<p-icon overrideColor :icon="!permissionLicenses.viewer ? 'upgrade' : 'blank'" />
								</div>
							</div>
						</r-pop-list-item>
						<r-pop-list-item 
							class="option-item"
							@click="setDefaultPermissionLevel('commenter', permissionLicenses.commenter)"
						>
							<div class="possible-checkmark">
								<r-standard-icon
									class="checkmark-icon"
									v-if="defaultPermissionKey === 'commenter'"
									icon="checkMark"
								></r-standard-icon>
							</div>
							<div class="default-item-container">
								<div class="item-title">Have comment access</div>
								<div class="item-subtitle">All users can view this Workbook and add comments, but they cannot add or edit any data.</div>
								<div class="item-icon-wrapper">
									<p-icon overrideColor :icon="!permissionLicenses.commenter ? 'upgrade' : 'blank'" />
								</div>
							</div>
						</r-pop-list-item>
						<r-pop-list-item 
							class="option-item"
							@click="setDefaultPermissionLevel('editor', permissionLicenses.editor)"
						>
							<div class="possible-checkmark">
								<r-standard-icon
									class="checkmark-icon"
									v-if="defaultPermissionKey === 'editor'"
									icon="checkMark"
								></r-standard-icon>
							</div>
							<div class="default-item-container">
								<div class="item-title">Have edit access</div>
								<div class="item-subtitle">All users can view this Workbook and add or edit its data.</div>
								<div class="item-icon-wrapper">
									<p-icon overrideColor :icon="!permissionLicenses.editor ? 'upgrade' : 'blank'" />
								</div>
							</div>
						</r-pop-list-item>
						<r-pop-list-item 
							class="option-item"
							@click="setDefaultPermissionLevel('owner', permissionLicenses.owner)"
						>
							<div class="possible-checkmark">
								<r-standard-icon
									class="checkmark-icon"
									v-if="defaultPermissionKey === 'owner'"
									icon="checkMark"
								></r-standard-icon>
							</div>
							<div class="default-item-container">
								<div class="item-title">Have owner access</div>
								<div class="item-subtitle">All users can edit this Workbook and its user permissions.</div>
								<div class="item-icon-wrapper">
									<p-icon overrideColor :icon="!permissionLicenses.owner ? 'upgrade' : 'blank'" />
								</div>
							</div>
						</r-pop-list-item>
					</r-pop-list>
				</r-pop-menu>
			</div>
		</div>
	</r-standard-modal-form>
</template>

<script>

import CloudStore from 'js/cloudstore/cloudstore';
import Identity from 'js/core/identity';
import Auth from 'js/background/auth';
import UserInvite from 'js/core/user-invite';
import {knowledgeBaseArticles} from 'js/education/on-demand-guidance/on-demand-guidance-manifest.js'
import ChangingInputAsyncResponseQueue from "js/common/queues/changing-input-async-response-queue.js";

import AccessControlUserItem from 'vue/share/access-control-user-item';

const MESSAGE_TIMEOUT = 6000; //milliseconds

export default {
	name: 'access-control-modal',
	$pubsub:{
		sheetViews:{
		}
	},
	data(){
		const vcSelf = this;
		return {
			permissionLicenses: {
				owner: false,
				editor: false,
				commenter: false,
				viewer: false
			},
			topMessage: null,
			shareWaiting: false,
			searchTerm: '',
			searchResults: null,
			emailValid: false,
			isFocused: false,
			loadingUsers: false,
			changedDefaultAccess: null,
			accessControlUsers: [],
			userDetails: null,
			newPermission: null,
			selectedResult: null,
			sharingAndPermissionsLink: knowledgeBaseArticles['sharingAndPermissions'] && knowledgeBaseArticles['sharingAndPermissions'].link || 'https://support.visor.us',
			searchTermQueue: new ChangingInputAsyncResponseQueue(500, 2000, function onExecute(onComplete, searchTerm){
				CloudStore.load({
					filters:{
						context: {
							user_guid:false,
							team_guid:false,
							core_context: false
						},
						meta: {
							type: {
								eq: "User",
							}
						},
						payload: {
							or: [{
								"emailAddress__icontains": {
									eq: searchTerm,
								},
								"givenName__icontains": {
									eq: searchTerm,
								},
								"familyName__icontains": {
									eq: searchTerm
								}
							}]
						}
					},
					options: {
						limit: 10,
						ordering: [{
							target: "payload",
							property: "emailAddress",
							order: "asc",
						}]
					}
				}, function withCloudStoreUserLoadResults(results) {
					if (results.length > 0) {
						vcSelf.$refs.resultPop.openPop();
					} else {
						vcSelf.$refs.resultPop.closePop();
					}
					vcSelf.searchResults = results;
					onComplete();
				});

				// CloudStore.search({
				// 	queryOptions:{
				// 		fetch:{
				// 			objects:true,
				// 			matchDetails:true,
				// 			indexedValues:true,
				// 		}
				// 	},
				// 	filters:{
				// 		context:{
				// 			core_context:false,
				// 			panel:false,
				// 			feature:false,	         
				// 			user_guid:false,
				// 			team_guid:false,		         
				// 		},
				// 		meta:{
				// 			type:{
				// 				eq:'user',
				// 			}
				// 		}
				// 	},
				// 	query:{
				// 		multi_match:{
				// 			query: searchTerm,
				// 			type: "cross_fields",
				// 			fields:['payload.body.i1_givenName^3', 'payload.body.i1_familyName^10', 'payload.body.i1_emailAddress'],
				// 		}
				// 	}
				// }, function withCloudStoreSpaceResults(results){
				// 	let resultObjects = results.results.sort(function sortScore(resultA, resultB){
				// 		return resultB.score - resultA.score;
				// 	}).map(function(resultItem){return resultItem.object});
					
				// 	if(resultObjects.length > 0){
				// 		vcSelf.$refs.resultPop.openPop();
				// 	}
				// 	else{
				// 		vcSelf.$refs.resultPop.closePop();
				// 	}
					
				// 	vcSelf.searchResults = resultObjects;
				// });
			}),
		}
	},
	mounted(){
		const vcSelf = this;
		// vcSelf.$refs.emailAddresses.focus();
		vcSelf.newPermission = 'viewer';
		vcSelf.userDetails = Auth.getUser();
		vcSelf.loadAccessControlUsers();

		const licenseManager = this.$root.systemState.licenseManager;
		licenseManager.verifyProvisionedEntitlementAvailability("Workbook.Roles.Viewer", null, function withEntitlement(hasEntitlement) {
			vcSelf.permissionLicenses.viewer = hasEntitlement;
		});
		licenseManager.verifyProvisionedEntitlementAvailability("Workbook.Roles.Commenter", null, function withEntitlement(hasEntitlement) {
			vcSelf.permissionLicenses.commenter = hasEntitlement;
		});
		licenseManager.verifyProvisionedEntitlementAvailability("Workbook.Roles.Editor", null, function withEntitlement(hasEntitlement) {
			vcSelf.permissionLicenses.editor = hasEntitlement;
		});
		licenseManager.verifyProvisionedEntitlementAvailability("Workbook.Roles.Owner", null, function withEntitlement(hasEntitlement) {
			vcSelf.permissionLicenses.owner = hasEntitlement;
		});
	},
	props:{
		dataStore:{
			required: true,
			type: Object,
		},
		onCloseCallback: {
			required: false,
			type: Function,
			default: null
		}
	},
	components:{
		AccessControlUserItem,
	},
	watch:{
		searchTerm(searchTerm, previousTerm){
			const vcSelf = this;
			if(vcSelf.selectedResult && vcSelf.selectedResult === searchTerm){
				// skip searching the selected user
				vcSelf.validateEmail();
				return;
			}

			if(vcSelf.selectedResult && searchTerm !== vcSelf.selectedResult){
				vcSelf.selectedResult = null;
			}

			vcSelf.searchTermQueue.inputChanged(searchTerm);
			vcSelf.validateEmail();
		},
		accessControlUserGUIDs(){
			this.loadAccessControlUsers();
		},
		permissionLevels(){
			this.newPermission = this.permissionLevels[0].key;
		},
	},
	computed:{
		shareableLink(){
			return window.location.href;
		},
		sharablePath(){
			return this.$route.path;
		},
		shareTitle(){
			return this.dataStore.title;
		},
		accessControlUserGUIDs(){
			if(this.dataStore.accessControl){
				return Object.keys(this.dataStore.accessControl).filter(function filterUserGUIDs(accessControlKey){
					return accessControlKey !== 'default';
				});
			}
		},
		defaultPermissionIcon(){
			const iconMap = {
				private: 'private',
				viewer: 'watchfulEye',
				commenter: 'watchfulEye',
				editor: 'editor',
				owner: 'owner',
			};

			if(this.defaultPermissionKey){
				return iconMap[this.defaultPermissionKey];
			}
			return iconMap.private;
		},
		defaultPermissionText(){
			const textMap = {
				private: 'All users need an invite to view',
				viewer: 'All users have view access',
				commenter: 'All users have comment access',
				editor: 'All users have edit access',
				owner: 'All users have owner access',
			};

			if(this.defaultPermissionKey){
				return textMap[this.defaultPermissionKey];
			}
			return textMap.private;
		},
		defaultPermissionKey(){
			if(this.dataStore.accessControl && this.dataStore.accessControl.default){
				return this.dataStore.accessControl.default.level;
			}
			else{
				return 'owner';
			}
		},
		accessControlOwners(){
			const vcSelf = this;
			if(this.sortedAccessControlUsers){
				return this.sortedAccessControlUsers.filter(function onlyOwners(userObj){
					return vcSelf.dataStore.accessControl[userObj.userGUID].level === 'owner';
				});
			}
			else{	
				return [];
			}
		},
		currentSelectedWorkspace(){
			return Identity.getIdentityProperty('UserGUID');
		},
		footerTitleText(){
			if(this.changedDefaultAccess !== null){
				if(this.changedDefaultAccess.wasSet){
					return "Workspace access updated";
				}
				else{
					return this.changedDefaultAccess.message;
				}
			}
			else{
				return this.workspaceName;
			}
		},
		workspaceName(){
			return this.$root.systemState.auth.currentWorkspaceLabel;
		},
		loadingUsersList(){
			return [null, null, null];
		},
		sortedAccessControlUsers(){
			const vcSelf = this;
			const sortLevelMap = {
				owner: 0,
				editor: 1,
				viewer: 2,
				private: 3,
			};

			if(vcSelf.accessControlUsers){
				return vcSelf.accessControlUsers.filter(function noLongerInAccessControl(userObj){
					return userObj && userObj.userGUID && vcSelf.dataStore.accessControl[userObj.userGUID] && vcSelf.dataStore.accessControl[userObj.userGUID].level;
				}).sort(function bringCurrentUserToTop(userA, userB){
					if(userA.userGUID === vcSelf.userDetails.user_guid){
						return -1;
					}
					else if(userB.userGUID === vcSelf.userDetails.user_guid){
						return 1;
					}
					else{
						// sort by access level (owner, editor, viewer), then by lastname/email
						if(vcSelf.dataStore.accessControl[userA.userGUID].level === vcSelf.dataStore.accessControl[userB.userGUID].level){
							// I see your schwartz is as big as mine
							let aCompare, bCompare;
							if(userA.familyName.length > 0){
								aCompare = userA.familyName;
							}
							else{
								aCompare = userA.emailAddress;
							}

							if(userB.familyName.length > 0){
								bCompare = userB.familyName;
							}
							else{
								bCompare = userB.emailAddress;
							}

							return aCompare.localeCompare(bCompare);
						}
						else{
							return sortLevelMap[vcSelf.dataStore.accessControl[userA.userGUID].level] - sortLevelMap[vcSelf.dataStore.accessControl[userB.userGUID].level];
						}
					}
				});
			}
		},
		newAccessLevelLabel(){
			const labelMap = {
				viewer: "View",
				commenter: "Comment",
				editor: "Edit",
				owner: "Owner",
			};

			if(this.newPermission){
				return labelMap[this.newPermission];
			}
			else{
				return null;
			}
		},
		permissionLevels(){
			let returnLevels = [{
				key: 'viewer',
				label: 'View access',
				description: 'This user can view this Workbook and comments, but cannot add or edit any comments or data.',
				permission: 'access_addViewer',
				hasEntitlement: this.permissionLicenses.viewer,
			}];

			if(this.workbookHasPermission('access_addCommenter')){
				returnLevels.push({ 
					key: 'commenter',
					label: 'Comment access',
					description: 'This user can view this Workbook and add comments but cannot edit any data.',
					permission: 'access_addCommenter',
					hasEntitlement: this.permissionLicenses.commenter
				});
			}

			if(this.workbookHasPermission('access_addEditor')){
				returnLevels.push({ 
					key: 'editor',
					label: 'Edit access',
					description: 'This user can view this Workbook and add or edit it’s comments and data.',
					permission: 'access_addEditor',
					hasEntitlement: this.permissionLicenses.editor
				});
			}

			if(this.workbookHasPermission('access_addOwner')){
				returnLevels.push({ 
					key: 'owner',
					label: 'Owner access',
					description: 'This user can edit this Workbook and it’s user permissions.',
					permission: 'access_addOwner',
					hasEntitlement: this.permissionLicenses.owner
				});
			}

			return returnLevels;
		},
		shareButtonEnabled(){
			return this.searchTerm.length > 0 && this.emailValid && !this.shareWaiting;
		},
	},
	methods:{
		closeModal(){
			this.$emit('closeModal');
			if (this.onCloseCallback) {
				this.onCloseCallback();
			}
		},
		copyLink(){
			this.$refs.link.select();
			document.execCommand('copy');
		},
		focused(){
			this.isFocused = true;
			if(this.searchTerm.length === 0){
				this.$refs.resultPop.openPop();
			}
		},
		blurred(){
			this.isFocused = false;			
		},
		resultSelected(result){
			const vcSelf = this;
			vcSelf.$refs.resultPop.closePop();
			vcSelf.searchTerm = result.emailAddress;
			vcSelf.selectedResult = result.emailAddress;
		},
		loadAccessControlUsers(){
			const vcSelf = this;

			vcSelf.loadingUsers = true;
			if(vcSelf.accessControlUserGUIDs && vcSelf.accessControlUserGUIDs.length > 0){
				CloudStore.load({
					filters:{
						context:{
							user_guid: false,
							team_guid: false,
							core_context: false,
						},
						meta:{
							type:{
								eq: "User",
							},
							bucket:{
								in: vcSelf.accessControlUserGUIDs,
							}
						},
					},
					options:{
						limit: 500,
					}
				}, function withLoadResults(results){
					vcSelf.loadingUsers = false;
					if(results){
						vcSelf.accessControlUsers = results;
					}
				});
			}
			else{
				vcSelf.loadingUsers = false;
			}
		},
		workbookHasPermission(accessRequestKey){
			if(this.dataStore){
				return this.dataStore.hasPermission(accessRequestKey);
			}
			else{
				Assert.throwError('Tried to get permission before workbook loaded');
			}
		},
		setNewAccessLevel(permission, hasEntitlement){
			if (hasEntitlement) {
				this.newPermission = permission;
			} else {
				this.$pubsub.modules.upgrade.pub("openModal", {
					flowName: "entitlementExhausted",
					attributes: {
						entitlementIdentifier: {
							owner: "Workbook.Roles.Owner",
							editor: "Workbook.Roles.Editor",
							commenter: "Workbook.Roles.Commenter",
							viewer: "Workbook.Roles.Viewer"
						}[permission]
					}
				});
			}
		},
		setDefaultPermissionLevel(permission, hasEntitlement){
			const vcSelf = this;
			if (hasEntitlement) {
				// set the message for the user
				if (vcSelf.changedDefaultAccess && vcSelf.changedDefaultAccess.timeout) {
					clearTimeout(vcSelf.changedDefaultAccess.timeout);
				}

				if (vcSelf.accessControlOwners.length === 0 && permission !== 'owner') {
					// dont let the user shoot themself in the foot
					vcSelf.changedDefaultAccess = {
						wasSet: false,
						message: "Please add at least one owner above before changing the global access level",
					};
				} else {
					// set the permission
					vcSelf.dataStore.setDefaultPermission(permission);
					vcSelf.changedDefaultAccess = {
						wasSet: true,
					};
				}
				
				vcSelf.changedDefaultAccess.timeout = setTimeout(function afterTimeout(){
					vcSelf.changedDefaultAccess = null;
				}, MESSAGE_TIMEOUT);
			} else {
				vcSelf.$pubsub.modules.upgrade.pub("openModal", {
					flowName: "entitlementExhausted",
					attributes: {
						entitlementIdentifier: {
							owner: "Workbook.Roles.Owner",
							editor: "Workbook.Roles.Editor",
							commenter: "Workbook.Roles.Commenter",
							viewer: "Workbook.Roles.Viewer"
						}[permission]
					}
				});
			}
			return;
		},
		validateEmail(){
			if(this.searchTerm.trim().length > 0){
				if(!(/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.searchTerm.trim()))){
					this.displayTopMessage({
						text: "Please enter a valid email address",
						warning: true,
					});
					this.emailValid = false;
					return false;
				}
				else{
					this.topMessage = null;
					this.emailValid = true;
					return true;
				}
			}
			else{
				this.emailValid = false;
				return false;
			}
		},
		displayTopMessage(message){
			const rvSelf = this;
			if(rvSelf.topMessage && rvSelf.topMessage.timeout){
				clearTimeout(rvSelf.topMessage.timeout);
			}
			rvSelf.topMessage = message;
			rvSelf.topMessage.timeout = setTimeout(function afterTimeout(){
				rvSelf.topMessage = null;
			}, MESSAGE_TIMEOUT);
		},
		addShareUser(){
			const vcSelf = this;

			if(vcSelf.shareWaiting){
				return;
			}
			vcSelf.shareWaiting = true;

			if(vcSelf.validateEmail()){
				const shareEmailAddress = vcSelf.searchTerm.trim().toLowerCase(); // Explicitly lower case the email address so that the function goes through correctly

				// dont let users "reinvite" themselves to remove permissions
				if(shareEmailAddress === vcSelf.userDetails.user_email){
					vcSelf.displayTopMessage({
						text: "You cannot change your own permission level.",
						warning: true,
					});
					vcSelf.shareWaiting = false;
					return;
				}

				let emailWasAlreadyOnThisDataStore = false;
				// lookup current access control user with this email
				for(let userIndex in vcSelf.accessControlUsers){
					if(vcSelf.accessControlUsers[userIndex].emailAddress === shareEmailAddress){
						// update this user, dont add them again
						emailWasAlreadyOnThisDataStore = true;

						vcSelf.searchTerm = '';
						vcSelf.dataStore.setUserPermission(vcSelf.accessControlUsers[userIndex].userGUID, vcSelf.newPermission);

						vcSelf.displayTopMessage({
							text: "Succesfully updated. Anyone else?",
							warning: false,
						});

						vcSelf.shareWaiting = false;
						break;
					}
				}

				if(!emailWasAlreadyOnThisDataStore){
					UserInvite.shareLinkWithUser([shareEmailAddress], this.shareTitle, this.sharablePath, function onLinkShared(newUserGUIDsByEmail){
						if(newUserGUIDsByEmail && newUserGUIDsByEmail[shareEmailAddress]){
							vcSelf.searchTerm = '';

							vcSelf.dataStore.setUserPermission(newUserGUIDsByEmail[shareEmailAddress], vcSelf.newPermission);

							vcSelf.displayTopMessage({
								text: "Succesfully shared. Anyone else?",
								warning: false,
							});
						}
						else{
							vcSelf.displayTopMessage({
								text: "We encountered an issue. Please try again.",
								warning: true,
							});
						}
						
						vcSelf.shareWaiting = false;
					});
				}
			}
			else{
				return;
			}
		},
		shareWithUsers(){
			const rvSelf = this;

			if(rvSelf.shareWaiting){
				return;
			}
			rvSelf.shareWaiting = true;

			let emailsValid = true;
			let emailAddressesArray = rvSelf.$refs.emailAddresses.value.split(',');
			emailAddressesArray = emailAddressesArray.map(function trimSpaces(email){
				return email.trim();
			});

			// check validity of email addresses
			for(let email of emailAddressesArray){
				if(!(/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))){
					emailsValid = false;
					break;
				}
			}

			if(!emailsValid){
				rvSelf.displayTopMessage({
					text: "One of the emails entered is invalid",
					warning: true,
				})

				rvSelf.shareWaiting = false;
				return;
			}
			else{
				UserInvite.shareLinkWithUser(emailAddressesArray, this.shareTitle, this.sharablePath, function onLinkShared(){
					rvSelf.$refs.emailAddresses.resetField();
					rvSelf.displayTopMessage({
						text: "Invite Sent! Share with another colleague?",
						warning: false,
					});
					rvSelf.shareWaiting = false;
				});
			}

		}
	}

}

</script>

<style scoped lang="scss">
/* Override title padding of parent: r-standard-modal-form */
.standard-modal-form {
	:nth-child(1) {
		:nth-child(1) {
			padding-right: 0px;
		}
	}
}

.access-control-modal-header{
	cursor: default;

	a {
		text-decoration: none;
		color: $p-color-blue-300;

		&:hover {
			text-decoration: underline;
		}
	}
}

.access-control-modal-body{
	width: 100%;

	.input-instruction {
		color: $p-color-grey-400;
	}

	.user-list-container{
		height: 290px;

		.user-list-title{
			font-family: $rvFontFamily;
			font-weight: 600;
			font-size: 13px;
			line-height: 19px;
			letter-spacing: 0.3px;
			color: $p-color-grey-400;
			margin-bottom: 6px;
		}

		.user-list{
			height: 310px;
			width: 313px;
			padding-right: 6px;
			overflow-y: scroll;

			&::-webkit-scrollbar {
				-webkit-appearance: none;
				width: 7px;
			}

			&::-webkit-scrollbar-thumb {
				border-radius: 4px;
				background-color: rgba(0,0,0,.5);
				-webkit-box-shadow: 0 0 1px rgba(255,255,255,.5);
			}
		}
	}
}

.search-container{
	padding-bottom: 12px;

	.search-input-container{
		@include flex(row, space-between);

		width: 100%;
		border: 1px solid $p-color-grey-300;
		margin-top: 6px;

		.search-input{
			background: transparent;
			border: none;
			outline: none;
			color: #000;		
			font-family: $rvFontFamily;
			font-size: 14px;
			line-height: 100%;
			letter-spacing: 0.15px;	
			height: 100%;
			padding: 11px 0px 10px 12px;
			width: 100%;

			&.show-new-permission-pop {
				width: calc(80% - 20px);
			}
		}

		.new-permission-pop{
			cursor: pointer;
			margin-right: 6px;

			.permission-selection-activator{
				@include flex(row, flex-end);
				width: 90px;

				&.disabled{
					cursor: default;
				}

				.permission-selection-activator-selector{
					@include flex(row);

					.permission-selection-activator-text{
						font-family: $rvFontFamily;
						font-size: 14px;
						line-height: 20px;
						text-align: right;
						letter-spacing: 0.01em;
						color: $p-color-grey-400;						
					}
				}
			}
			.permission-selection-item{
				height: 90px;

				.possible-checkmark{
					min-width: 39px;
					padding-right: 9px;
				}

				.permission-item-container{
					@include flex(column, flex-start, flex-start);
					position: relative;

					.permission-label{
						font-family: $rvFontFamily;
						font-size: 14px;
						line-height: 20px;
						letter-spacing: 0.01em;
						color: $p-color-grey-600;
					}
					.permission-description{
						font-family: $rvFontFamily;
						font-size: 13px;
						line-height: 19px;
						letter-spacing: 0.01em;
						color: $p-color-grey-400;
					}
					
					.permission-icon-wrapper {
						position: absolute;
						top: 0px;
						right: 0px;
						color: $p-color-yellow-300;
						width: 20px;
						height: 20px;
					}
				}
			}
		}
	}
}

.search-results{
	width: 330px;
	padding: 9px 0;

	.search-display{
		font-weight: 600;
		font-size: 12px;
		line-height: 18px;
		letter-spacing: 0.3px;
		color: #AEB4BE;
		padding: 8px 21px 5px 21px;

	}

	.search-pop-prompt{
		font-size: 14px;
		line-height: 100%;
		letter-spacing: 0.15px;
		color: #2C2C38;
		padding: 17px 21px 14px 21px;
	}

	.search-result-item{
		height: 39px;

		&.double-label{
			height: 60px;
		}

		.search-result{
			@include flex(column, flex-start, flex-start);
			
			.result-label-top{
				font-family: $rvFontFamily;
				font-size: 14px;
				line-height: 20px;
				letter-spacing: 0.15px;
				color: $p-color-grey-600;
				white-space: nowrap;
				text-overflow: ellipsis;
				overflow: hidden;
			}

			.result-label-sub{
				font-family: $rvFontFamily;
				font-size: 13px;
				line-height: 19px;
				letter-spacing: 0.15px;
				color: $p-color-grey-500;
				white-space: nowrap;
				text-overflow: ellipsis;
				overflow: hidden;
			}
		}
	}
}

.save-button{
	width: 100%;
	margin-bottom: 12px;

	.button-text{
		font-weight: 600!important;
	}
}

.footer-area{
	border-top: 1px solid #BAC8D4;
	background-color: $p-color-grey-100;
	margin-top: 24px;
	font-weight: normal;
	font-size: 13px;
	line-height: 20px;
	letter-spacing: 0.1px;
	cursor: default;
	color: #9CA3B2;
	
	position: relative;
	top: 24px;
	left: -18px;
	width: 301px;
	padding: 0 18px 9px 18px;

	.footer-title{
		font-family: $rvFontFamily;
		font-weight: 600;
		font-size: 13px;
		line-height: 19px;
		letter-spacing: 0.3px;
		color: $p-color-grey-400;
		padding-top: 12px;
		padding-bottom: 6px;
		transition: all 500ms ease;

		&.success-message{
			color: $p-color-teal-300;
		}

		&.warn-message{
			color: $p-color-red-300;
		}
	}

	.footer-activator{
		@include flex(row);

		height: 40px;
		cursor: pointer;
		&.disabled{
			cursor: default;
		}

		.footer-activator-text{
			font-family: $rvFontFamily;
			font-size: 14px;
			line-height: 20px;
			color: $p-color-grey-600;
			margin: 0 6px 0 9px;
		}
	}

	.options-menu{

		.option-item{
			@include flex(column, flex-start, flex-start);

			height: 99px;

			.possible-checkmark{
				min-width: 39px;
				padding-right: 9px;
			}

			.default-item-container{
				@include flex(column, flex-start, flex-start);
				position: relative;

				.item-title{
					font-family: $rvFontFamily;
					font-size: 14px;
					line-height: 20px;
					letter-spacing: 0.01em;
					color: $p-color-grey-600;
					margin-top: 9px;
				}
				.item-subtitle{
					font-family: $rvFontFamily;
					font-size: 13px;
					line-height: 19px;
					letter-spacing: 0.01em;
					color: $p-color-grey-400;
					margin-bottom: 9px;
				}
				.item-icon-wrapper {
					position: absolute;
					top: 0px;
					right: 0px;
					color: $p-color-yellow-300;
					width: 20px;
					height: 20px;
				}
			}
		}
	}
}

.shareable-link-container{
	@include flex(row, space-between, flex-start);

	border-bottom: 1px solid $p-color-grey-300;
	height: 26px;

	margin-top: 6px;

	.shareable-link{
		@include flex(row, flex-start, center);

		width: 100%;
		font-weight: normal;
		font-size: 14px;

		flex-grow: 1;

		color: $p-color-grey-600;

		outline: none;
		border: none; 

		// padding: 6px 0px 3px 9px;
		box-sizing: border-box;

		white-space: nowrap;
		overflow: hidden;

		input{
			all: inherit;
			border: none;
			outline: none;
			user-select: all;
			width: 100%;
			height: 100%;
			text-overflow: ellipsis;

			padding-left: 8px;
		}
	}

	.copy-button{
		margin-left: 12px;
		font-weight: 600;
		font-size: 17px;
		line-height: 19px;
		color: $p-color-blue-500;

		@include clickable;
	}
}

.top-message-container,
.bottom-message-container{
	@include flex(row);

	height: 18px;
	width: 100%;
	margin-top: 6px;
	margin-bottom: 18px;


	.invite-message{
		font-style: normal;
		font-weight: normal;
		font-size: 13px;
		line-height: 18px;
		margin-top: 9px;
		letter-spacing: 0.15px;
		color: $p-color-teal-300;
		cursor: default;

		&.warning{
			color: $p-color-red-300;
		}
	}
}

@keyframes loading {
	0% {transition:none;}
	50% {background-color:#E2E5F2;transition: all 0.3s ease-out;}
	100% {transition:none;}
}
.loading-block{
	margin: 10px 0;
	height: 20px;
	width: 100%;
	background-color: #f2f4fa;
	animation-name: loading;
	animation-duration: 2s;
	animation-iteration-count: infinite;
	animation-timing-function: linear;
	display: flex;
	flex-direction: row;
	align-items: center;
	justify-content: flex-start;
}

</style>
